import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const focusAreas = [
  {
    title: "Commerce + Media",
    body: "Bridging audience attention with transactional intent. Building infrastructure where content, community and commerce converge.",
  },
  {
    title: "Advertising + Attribution",
    body: "Closed-loop measurement, performance-driven audience targeting, and modern attribution across open web and walled gardens.",
  },
  {
    title: "Live Shopping + Distribution",
    body: "Creator-led sales, storefront operations, live production and marketplace growth across TikTok Shop, Whatnot, Amazon Live, Palmstreet and eBay Live.",
  },
  {
    title: "Creator Economy + Performance Marketing",
    body: "Operationalizing creators as a distribution channel — measurable, repeatable, scalable.",
  },
  {
    title: "Retail + Emerging Platforms",
    body: "Retail media monetization, connected TV commerce, and the next wave of shoppable surfaces.",
  },
];

const differentiators = [
  { num: "01", title: "Operator-Led", body: "Built by operators who have scaled companies from startup stage through commercial execution and exit." },
  { num: "02", title: "Revenue First", body: "Practical execution and measurable growth — not vanity metrics or theoretical TAM." },
  { num: "03", title: "Cross-Platform Expertise", body: "Commerce infrastructure, ad ecosystems, live selling, marketplaces, audience acquisition, attribution systems." },
  { num: "04", title: "Speed & Agility", body: "Designed to move quickly, test aggressively, and scale proven models rapidly." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* INTRO */}
      <section className="container-x pt-12 md:pt-20 pb-24 md:pb-32">
        <p className="eyebrow mb-8">About SamStella</p>
        <h1
          className="hero-headline text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] max-w-[16ch]"
          data-testid="about-headline"
        >
          Ownership platform. Execution engine.
        </h1>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7 md:col-start-6 text-base md:text-lg font-light leading-relaxed text-foreground/80 space-y-6">
            <p>
              SamStella is a strategic investment, incubation and business development firm focused on scaling modern commerce, media, advertising and technology businesses.
            </p>
            <p>
              Founded by Andrew S. Reis, SamStella operates as both an ownership platform and an execution engine — identifying high-growth opportunities, building operational infrastructure, driving revenue acceleration, and helping companies navigate commercialization at scale.
            </p>
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="border-t border-border">
        <div className="container-x py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">What we do</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">
                Core focus areas.
              </h2>
            </div>
            <div className="md:col-span-8 text-base md:text-lg font-light leading-relaxed text-foreground/80">
              SamStella is structured to move faster than traditional holding companies by combining capital, operational support, strategic relationships, and go-to-market execution under one umbrella.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {focusAreas.map((f, i) => (
              <div
                key={f.title}
                data-testid={`about-focus-${i}`}
                className={`p-8 md:p-10 border-border ${i % 2 === 0 ? "md:border-r" : ""} border-t ${i >= focusAreas.length - 1 ? "border-b" : ""}`}
              >
                <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-4">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-4">{f.title}</h3>
                <p className="text-base font-light leading-relaxed text-foreground/80 max-w-md">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-background/60 mb-4">
              Core Philosophy
            </p>
          </div>
          <div className="md:col-span-8">
            <p
              className="font-display text-3xl md:text-5xl tracking-tight leading-[1.1]"
              data-testid="about-philosophy"
            >
              The future of commerce is <em className="not-italic underline decoration-1 underline-offset-8">interactive</em>, <em className="not-italic underline decoration-1 underline-offset-8">performance-driven</em>, <em className="not-italic underline decoration-1 underline-offset-8">creator-led</em>, <em className="not-italic underline decoration-1 underline-offset-8">data-informed</em> — and distributed across multiple platforms simultaneously.
            </p>
            <p className="mt-10 text-base md:text-lg font-light leading-relaxed max-w-2xl text-background/70">
              We invest where audience attention is moving and build infrastructure for how monetization will evolve around it — connecting consumers to transactions at scale.
            </p>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="border-t border-border">
        <div className="container-x py-24 md:py-32">
          <div className="mb-14">
            <p className="eyebrow mb-4">Differentiators</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight max-w-[18ch]">
              Why founders, brands and platforms partner with us.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            {differentiators.map((d, i) => (
              <div
                key={d.num}
                data-testid={`about-diff-${d.num}`}
                className={`p-8 md:p-10 border-border ${i % 2 === 0 ? "md:border-r" : ""} border-t ${i >= differentiators.length - 1 ? "border-b" : ""}`}
              >
                <div className="flex items-start gap-6">
                  <div className="font-display text-5xl md:text-6xl leading-none">{d.num}</div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">{d.title}</h3>
                    <p className="text-base font-light leading-relaxed text-foreground/80 max-w-md">{d.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 md:sticky md:top-28 md:self-start">
            <p className="eyebrow mb-4">Our Story</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">
              The gap between concept and scale.
            </h2>
          </div>
          <div className="md:col-span-8 text-base md:text-lg font-light leading-relaxed text-foreground/80 space-y-6">
            <p>
              SamStella, LLC was built around a simple idea: great businesses do not fail because of lack of vision — they fail because execution, distribution, monetization, and timing rarely align at the same moment.
            </p>
            <p>
              Andrew’s career has consistently centered around identifying where industries were heading before they fully arrived — from early digital advertising infrastructure and online media monetization to today’s convergence of social commerce, creator-driven distribution, live shopping, retail media, and performance-based advertising ecosystems.
            </p>
            <p>
              Over the years, SamStella evolved from a private investment vehicle into a broader strategic platform focused on helping companies accelerate growth, unlock distribution, build revenue channels, and commercialize emerging opportunities faster than traditional organizations typically can.
            </p>
            <p>
              Unlike traditional investment firms, SamStella operates inside the businesses it supports. The company works closely with founders, platforms, brands, retailers and operators to drive commercialization, partnerships, infrastructure development, audience growth and monetization strategies.
            </p>
            <p className="pt-4 font-display text-xl md:text-2xl tracking-tight leading-snug text-foreground">
              Build real businesses. Focus on revenue. Move quickly. Stay adaptable. Create measurable outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="container-x py-20 md:py-28 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-[20ch]">
            See what we’ve built and operated.
          </h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/portfolio" className="btn-primary" data-testid="about-cta-portfolio">
              View Portfolio <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="about-cta-contact">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
